import { useState, useCallback } from 'react';
import { apiFetch } from '../../../context/AuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Fields that make up a policy's shape — used both to seed a fresh draft
// and to diff draft-vs-saved for the "unsaved changes" indicator.
const DEFAULT_DRAFT = {
  prefix: '',
  separator: '-',
  suffix: '',
  strategy: 'SEQUENTIAL',
  bodyLength: 4,
  padWithZeros: true,
  startAt: 1,
  alphabet: '',
  casing: 'UPPER',
  template: '',
  isLocked: false,
};

const DRAFT_FIELDS = Object.keys(DEFAULT_DRAFT);

export const useCodePolicy = (showMessage) => {
  // `policy` mirrors what the server has saved (null = this client has no
  // policy yet and is on the legacy fallback generator). `draft` is the
  // form's working copy — always populated, even before a policy exists,
  // so the form has something to render from the very first paint.
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewCodes, setPreviewCodes] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [warnings, setWarnings] = useState([]);

  const fetchCodePolicy = useCallback(async (clientName) => {
    if (!clientName) return;
    try {
      setLoading(true);
      setWarnings([]);
      const response = await apiFetch(
        `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}/code-policy`,
        { method: 'GET' },
      );
      const data = await response.json();
      const fetched = data?.data || null;
      setPolicy(fetched);
      setDraft(fetched ? { ...DEFAULT_DRAFT, ...fetched } : DEFAULT_DRAFT);
    } catch (error) {
      console.error('Failed to fetch code policy:', error);
      showMessage('error', 'Failed to load code policy');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const resetDraft = () => {
    setDraft(policy ? { ...DEFAULT_DRAFT, ...policy } : DEFAULT_DRAFT);
  };

  const isDirty = policy
    ? DRAFT_FIELDS.some((field) => String(draft[field] ?? '') !== String(policy[field] ?? ''))
    : Boolean(draft.prefix?.trim());

  const previewDraft = useCallback(async () => {
    if (!draft.prefix?.trim()) {
      setPreviewCodes([]);
      setPreviewError('');
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewError('');
      const response = await apiFetch(`${BASE_URL}/api/configurations/code-policy/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, count: 5 }),
      });
      const data = await response.json();
      if (!response.ok) {
        setPreviewCodes([]);
        setPreviewError(data?.message || 'Could not preview this format');
        return;
      }
      setPreviewCodes(data?.data?.codes || []);
    } catch (error) {
      console.error('Failed to preview code policy:', error);
      setPreviewCodes([]);
      setPreviewError('Could not preview this format');
    } finally {
      setPreviewLoading(false);
    }
  }, [draft]);

  const saveCodePolicy = async (clientName) => {
    if (!clientName) return false;
    try {
      setSaving(true);
      const response = await apiFetch(
        `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}/code-policy`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(draft),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        showMessage('error', data?.message || 'Failed to save code policy');
        if (Array.isArray(data?.errors)) {
          setWarnings([]);
        }
        return false;
      }
      setPolicy(data?.data || null);
      setDraft(data?.data ? { ...DEFAULT_DRAFT, ...data.data } : DEFAULT_DRAFT);
      setWarnings(data?.warnings || []);
      showMessage('success', 'Code policy saved successfully!');
      return true;
    } catch (error) {
      console.error('Failed to save code policy:', error);
      showMessage('error', 'Failed to save code policy');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    policy,
    draft,
    loading,
    saving,
    isDirty,
    previewCodes,
    previewLoading,
    previewError,
    warnings,
    fetchCodePolicy,
    updateDraft,
    resetDraft,
    previewDraft,
    saveCodePolicy,
  };
};

export { DEFAULT_DRAFT };
